import { CSSProperties, FC, lazy, Suspense } from 'react'

import { clsx } from 'clsx'

import {
  FormControl,
  FormControlProps,
} from '@tribeplatform/react-ui-kit/FormControl'
import { inputStyles } from '@tribeplatform/react-ui-kit/Input'

import { getEditorPollIds } from '../Composer/extensions/poll/utils.js'
import { FieldInputProps } from '../CustomField/type.js'

const Composer = lazy(() =>
  import('../Composer/Composer.js').then(m => ({ default: m.Composer })),
)
const ComposerControls = lazy(() =>
  import('../Composer/components/ComposerControls.js').then(m => ({
    default: m.ComposerControls,
  })),
)
const ComposerProvider = lazy(() =>
  import('../Composer/ComposerProvider.js').then(m => ({
    default: m.ComposerProvider,
  })),
)

export type FormControlHtmlEditorProps = FormControlProps & {
  value?: string
  onChange?: (value: string) => void
  className?: string
  style?: CSSProperties
  meta?: FieldInputProps['meta']
}

export const FormControlHtmlEditor: FC<FormControlHtmlEditorProps> = ({
  placeholder,
  name,
  value,
  error,
  invalid,
  label,
  helperText,
  onChange,
  className,
  style,
  meta,
}) => {
  return (
    <div className={clsx('space-y-1', className)}>
      {label && <FormControl.Label htmlFor={name}>{label}</FormControl.Label>}
      <Suspense fallback={<></>}>
        <ComposerProvider
          content={value}
          placeholder={placeholder}
          editorOptions={{
            // Seed on create so polls already in the content (e.g. when editing)
            // are reported even if the user never touches this field.
            onCreate: ({ editor }) => {
              meta?.composer?.onPollIdsChange?.(getEditorPollIds(editor))
            },
            onUpdate: ({ editor }) => {
              onChange(editor.isEmpty ? '' : editor.getHTML())

              // instead of prop drilling and passing the meta along the way, we could also use formContext to set value directly, but this approach is more type safe and as we specified it as optional, we guarantee that nowhere will break
              meta?.composer?.onPollIdsChange?.(getEditorPollIds(editor))
            },
          }}
          poll={meta?.composer?.poll}
          key={placeholder}
        >
          <div
            style={style}
            className={clsx(
              inputStyles({
                invalid,
              }),
              'flex flex-col gap-3 relative',
            )}
          >
            <Composer minLines={8} />
            <ComposerControls />
          </div>
        </ComposerProvider>
      </Suspense>
      <FormControl.Meta
        invalid={invalid}
        error={error}
        helperText={helperText}
      />
    </div>
  )
}
